import * as React from 'react';
// mui
import {
  useMediaQuery,
  useTheme,
  FormControl,
  MenuItem,
  Select,
  Slider,
  Grid,
} from '@mui/material';
// dayjs
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
// types
import { Mark } from '@mui/material/Slider/useSlider.types';

interface ExcuseDurationSelectProps {
  onChange: (interval: string) => void;
}

const durationMarks = {
  minutes: [
    {
      value: 5,
      label: '5m',
    },
    {
      value: 10,
      label: '10m',
    },
    {
      value: 15,
      label: '15m',
    },
    {
      value: 30,
      label: '30m',
    },
    {
      value: 45,
      label: '45m',
    },
    {
      value: 60,
      label: '60m',
    },
  ],
  hours: [
    {
      value: 1,
      label: '1h',
    },
    {
      value: 2,
      label: '2h',
    },
    {
      value: 3,
      label: '3h',
    },
    {
      value: 6,
      label: '6h',
    },
    {
      value: 12,
      label: '12h',
    },
    {
      value: 24,
      label: '24h',
    },
  ],
  days: [
    {
      value: 1,
      label: '1d',
    },
    {
      value: 2,
      label: '2d',
    },
    {
      value: 3,
      label: '3d',
    },
    {
      value: 4,
      label: '4d',
    },
    {
      value: 5,
      label: '5d',
    },
    {
      value: 6,
      label: '6d',
    },
    {
      value: 7,
      label: '7d',
    },
    {
      value: 10,
      label: '10d',
    },
    {
      value: 14,
      label: '14d',
    },
  ],
  weeks: [
    {
      value: 1,
      label: '1w',
    },
    {
      value: 2,
      label: '2w',
    },
    {
      value: 3,
      label: '3w',
    },
    {
      value: 4,
      label: '4w',
    },
  ],
};

const intervalOptions = ['Minutes', 'Hours', 'Days', 'Weeks'];

export default function ExcuseDurationSelect(props: ExcuseDurationSelectProps) {
  // props
  const onChangeCallback = props.onChange;

  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentInterval, setCurrentInterval] = React.useState<string>('Minutes');
  const [currentDuration, setCurrentDuration] = React.useState<number>(1);

  const findMax = (): number => {
    if (currentInterval === 'Minutes') {
      return 60;
    } else if (currentInterval === 'Hours') {
      return 24;
    } else if (currentInterval === 'Days') {
      return 14;
    } else if (currentInterval === 'Weeks') {
      return 4;
    } else {
      return 1;
    }
  }

  const handleLocalChange = () => {
    let interval: string = currentInterval.toLowerCase();
    if (currentDuration === 1) { // if plural not appropriate
      interval = interval.slice(0, -1); // remove 's' at end of string
    }
    const intervalString = `${currentDuration} ${interval}`;
    onChangeCallback(intervalString);
  }

  const fetchMarks = (): Array<Object> => {
    if (currentInterval === 'Minutes') {
      return durationMarks.minutes;
    } else if (currentInterval === 'Hours') {
      return durationMarks.hours;
    } else if (currentInterval === 'Days') {
      return durationMarks.days;
    } else if (currentInterval === 'Weeks') {
      return durationMarks.weeks;
    } else {
      return [];
    }
  }

  React.useEffect(() => {
    // when interval or duration changed, pass interval string through callback
    handleLocalChange();
  }, [currentDuration, currentInterval]);

  return (
    <FormControl fullWidth>
      <Grid container spacing={3}>
        <Grid size={8} sx={{ mb: 3, pl: 2 }}>
          <Slider
            aria-label="Time"
            sx={{ mt: 1.5 }}
            value={currentDuration}
            onChange={(e: any) => { setCurrentDuration(e.target.value);}}
            marks={fetchMarks() as Mark[]}
            defaultValue={1}
            min={1}
            max={findMax()}
            step={1}
            valueLabelDisplay="auto"
          />
        </Grid>

        <Grid size={4}>
          <Select
            fullWidth
            value={currentInterval}
            onChange={(e) => { setCurrentInterval(e.target.value); }}
          >
            { intervalOptions.map((duration: any, i: number) => (
              <MenuItem key={i} value={duration}>{duration}</MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
    </FormControl>
  );
}